import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Cancel, Save } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import {
  useCreateUnitHook,
  useUpdateUnitHook,
} from "../../../hooks/useLessonPlanHook";
import { logger } from "../../../utils/common";
import {
  TLessonPlanUnit,
  TCreateUnitRequest,
} from "../../../apis/lessonPlan/lessonPlan.interfaces";

const unitSchema = z.object({
  unitNumber: z.coerce
    .number({ invalid_type_error: "Unit number is required" })
    .int("Unit number must be a whole number")
    .min(1, "Unit number must be at least 1"),
  title: z
    .string()
    .min(2, "Title must be at least 2 characters")
    .max(200, "Title must not exceed 200 characters"),
  description: z
    .string()
    .max(2000, "Description must not exceed 2000 characters")
    .optional()
    .or(z.literal("")),
  duration: z
    .union([
      z.coerce.number().int("Duration must be a whole number").min(1),
      z.literal(""),
    ])
    .optional(),
  documentIdsText: z
    .string()
    .optional()
    .refine(
      (raw) => {
        if (!raw) return true;
        const lines = raw
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        return lines.length <= 50;
      },
      "At most 50 document IDs allowed",
    ),
});

type UnitFormData = z.infer<typeof unitSchema>;

interface ManageUnitDialogProps {
  open: boolean;
  onClose: () => void;
  lessonPlanId: string;
  unit?: TLessonPlanUnit | null;
}

const buildDefaults = (unit?: TLessonPlanUnit | null): UnitFormData => ({
  unitNumber: unit?.unitNumber ?? 1,
  title: unit?.title ?? "",
  description: unit?.description ?? "",
  duration: unit?.duration ?? "",
  documentIdsText: unit?.documentIds?.join("\n") ?? "",
});

const ManageUnitDialog: FC<ManageUnitDialogProps> = ({
  open,
  onClose,
  lessonPlanId,
  unit,
}) => {
  const isEditMode = !!unit;
  const createMutation = useCreateUnitHook();
  const updateMutation = useUpdateUnitHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UnitFormData>({
    resolver: zodResolver(unitSchema),
    defaultValues: buildDefaults(unit),
  });

  useEffect(() => {
    if (open) reset(buildDefaults(unit));
  }, [open, unit, reset]);

  const onSubmit = async (data: UnitFormData) => {
    const documentIds = (data.documentIdsText ?? "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload: TCreateUnitRequest = {
      unitNumber: data.unitNumber,
      title: data.title,
      description: data.description || undefined,
      duration:
        typeof data.duration === "number" ? data.duration : undefined,
      documentIds: documentIds.length ? documentIds : undefined,
    };

    try {
      if (isEditMode && unit) {
        await updateMutation.mutateAsync({
          lessonPlanId,
          unitId: unit.id,
          request: payload,
        });
        logger.show_success("Unit updated.");
      } else {
        await createMutation.mutateAsync({ lessonPlanId, request: payload });
        logger.show_success("Unit created.");
      }
      onClose();
    } catch (err) {
      logger.show_error(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? "update" : "create"} unit.`,
      );
    }
  };

  const activeMutation = isEditMode ? updateMutation : createMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="manage-unit-dialog"
    >
      <DialogTitle>{isEditMode ? "Edit Unit" : "Add Unit"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "Failed to save unit."}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="unitNumber"
              id="unit-number"
              label="Unit Number"
              required
              type="number"
              errorMessage={errors.unitNumber?.message}
              inputProps={{ min: 1 }}
            />
            <ControlledTextField
              control={control}
              name="title"
              id="unit-title"
              label="Title"
              required
              errorMessage={errors.title?.message}
            />
            <ControlledTextField
              control={control}
              name="description"
              id="unit-description"
              label="Description"
              multiline
              rows={3}
              errorMessage={errors.description?.message}
            />
            <ControlledTextField
              control={control}
              name="duration"
              id="unit-duration"
              label="Duration (minutes)"
              type="number"
              errorMessage={errors.duration?.message}
              inputProps={{ min: 1 }}
            />
            <ControlledTextField
              control={control}
              name="documentIdsText"
              id="unit-document-ids"
              label="Document IDs (one per line)"
              multiline
              rows={3}
              errorMessage={errors.documentIdsText?.message}
              helperText="Optional. Up to 50 entries."
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
          >
            {isLoading
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
                ? "Save Changes"
                : "Create Unit"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageUnitDialog;
